import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { zhTW } from "date-fns/locale";

/**
 * 從 ISO 8601 字串中抓取時區偏移量部分
 * @param {string} dateStr - 待處理的日期字串
 * @returns {string | null} 回傳偏移量字串 (如 '+08:00', '-05:00', 'Z')，若不符合則回傳 null
 */
function extractOffset(dateStr) {
  const match = dateStr.match(/(Z|[+-]\d{2}:\d{2})$/);
  return match?.[1] ?? null;
}

/**
 * 將帶有時區偏移量的 ISO 字串轉換為指定格式，並忠實呈現該偏移量之當地時間
 * 此函式會解析字串中的偏移量（如 +08:00），確保輸出的時間不隨執行環境（瀏覽器/伺服器）的時區改變
 * 若輸入無效格式、空值或缺少時區資訊，則回傳空字串
 *
 * @param {string | null | undefined} dateStr - ISO 8601 格式字串 (例如: '2026-02-07T00:23:44+08:00')
 * @param {string} formatStr - 輸出的格式字串，預設為 'yyyy-MM-dd HH:mm:ss'
 * @param {object} options - 格式化選項，預設使用台灣中文 locale
 * @returns {string} 格式化後的日期字串，若解析失敗則回傳空字串
 *
 * @example
 * // 輸出: "2026-02-07 00:23:44"
 * formatWithOffset('2026-02-07T00:23:44+08:00')
 *
 * @example
 * // 搭配空值合併運算子使用
 * formatWithOffset(data?.tranDate) || format(new Date(), 'yyyy-MM-dd HH:mm:ss')
 */
export function formatWithOffset(dateStr, formatStr = "yyyy-MM-dd HH:mm:ss", options = { locale: zhTW }) {
  // 1. 處理 null、undefined 或空字串
  if (!dateStr) return "";

  const date = parseISO(dateStr);
  const offset = extractOffset(dateStr);

  // 2. 驗證日期合法性且必須包含時區偏移量
  if (!isValid(date) || !offset) {
    return "";
  }

  // 3. 使用 formatInTimeZone 強制以該偏移量視角進行格式化
  return formatInTimeZone(date, offset, formatStr, options);
}
